import type { DisposePool } from "@phosphor/utils";
import type { ILogger } from "librarylog";
import { Socket } from "../presence-client/index.ts";

type CursorPosition = {
  anchor_id: string;
  rel_x: number;
  rel_y: number;
  anchor_width?: number;
  anchor_height?: number;
};

type PresenceUser = {
  name: string;
  cursor?: CursorPosition;
};

export type CursorEvents = {
  cursor_move: {
    user_id: string;
    anchor_id: string;
    rel_x: number;
    rel_y: number;
    anchor_width?: number;
    anchor_height?: number;
    name?: string;
  };
  presence_state: {
    users: Record<string, PresenceUser>;
  };
  presence_diff: {
    joins: Record<string, PresenceUser>;
    leaves: Record<string, PresenceUser>;
  };
};

export type RemoteCursor = {
  userId: string;
  anchorId: string;
  relX: number;
  relY: number;
  anchorWidth?: number;
  anchorHeight?: number;
  name?: string;
};

export function setupPresence(logger: ILogger, pool: DisposePool, onCursorsUpdate: (cursors: RemoteCursor[]) => void) {
  const presenceEndpoint = process.env.PRESENCE_SERVER_ENDPOINT || "ws://0.0.0.0:3000/socket";
  const socket = new Socket(presenceEndpoint);

  // Track remote cursors
  const remoteCursors = new Map<string, RemoteCursor>();

  // Generate unique user ID for this session
  const userId = `user_${Math.random().toString(36).substring(2, 11)}_${Date.now()}`;

  // Track local cursor state
  let lastBroadcastTime = 0;
  const BROADCAST_THROTTLE_MS = 50; // Throttle cursor updates
  let updateScheduled = false;

  // Page anchor constant and precompute element
  const PAGE_ANCHOR_ID = "__page__";
  const pageAnchorElement = document.querySelector(`[data-presence-anchor="${PAGE_ANCHOR_ID}"]`);

  const updateCursors = () => {
    if (updateScheduled) return;
    updateScheduled = true;
    requestAnimationFrame(() => {
      updateScheduled = false;
      onCursorsUpdate(Array.from(remoteCursors.values()));
    });
  };

  socket
    .connect()
    .then(() => {
      logger._debug("Connected to presence server");

      const channel = socket.channel<CursorEvents>("cursors:demo");

      // Join without JWT for demo (anonymous)
      channel
        .join(
          "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJkZXYtdXNlciIsIm5hbWUiOiJEZXZlbG9wZXIiLCJhdmF0YXIiOm51bGwsImFsbG93ZWRfZXZlbnRzIjpbImN1cnNvcl9tb3ZlIiwic2VsZWN0aW9uX2NoYW5nZSIsInR5cGluZyJdLCJleHAiOjE3OTE2Nzg2MjF9.yc-th1oAFIMKrMaG0ejerRfLoAIMZ2BAFre245W5rvY",
        )
        .then(() => {
          logger._debug("Joined cursors channel");

          // Listen for other users' cursor movements
          channel.on("cursor_move", (payload) => {
            if (payload.user_id) {
              remoteCursors.set(payload.user_id, {
                userId: payload.user_id,
                anchorId: payload.anchor_id,
                relX: payload.rel_x,
                relY: payload.rel_y,
                anchorWidth: payload.anchor_width,
                anchorHeight: payload.anchor_height,
                name: payload.name,
              });
              updateCursors();
            }
          });

          // Handle presence state (initial list of users)
          channel.on("presence_state", (payload) => {
            remoteCursors.clear();
            for (const [userId, userData] of Object.entries(payload.users)) {
              if (userData.cursor) {
                remoteCursors.set(userId, {
                  userId,
                  anchorId: userData.cursor.anchor_id,
                  relX: userData.cursor.rel_x,
                  relY: userData.cursor.rel_y,
                  anchorWidth: userData.cursor.anchor_width,
                  anchorHeight: userData.cursor.anchor_height,
                  name: userData.name,
                });
              }
            }
            updateCursors();
          });

          // Handle presence diff (users joining/leaving)
          channel.on("presence_diff", (payload) => {
            // Remove leaving users
            if (payload.leaves) {
              for (const userId of Object.keys(payload.leaves)) {
                remoteCursors.delete(userId);
              }
            }
            // Add joining users
            if (payload.joins) {
              for (const [userId, userData] of Object.entries(payload.joins)) {
                if (userData.cursor) {
                  remoteCursors.set(userId, {
                    userId,
                    anchorId: userData.cursor.anchor_id,
                    relX: userData.cursor.rel_x,
                    relY: userData.cursor.rel_y,
                    anchorWidth: userData.cursor.anchor_width,
                    anchorHeight: userData.cursor.anchor_height,
                    name: userData.name,
                  });
                }
              }
            }
            updateCursors();
          });

          // Track local cursor and broadcast
          const handleMouseMove = (event: MouseEvent) => {
            const now = Date.now();
            if (now - lastBroadcastTime < BROADCAST_THROTTLE_MS) {
              return; // Throttle
            }

            // Guard: ensure target is an element
            if (!(event.target instanceof Element)) {
              return;
            }

            // Always prefer closest anchor over page anchor
            const closestAnchor = event.target.closest("[data-presence-anchor]");
            const anchorElement = closestAnchor ?? pageAnchorElement;

            if (!anchorElement) {
              return; // No anchor found, skip
            }

            const anchorId = anchorElement.getAttribute("data-presence-anchor");
            if (!anchorId) {
              return;
            }

            // Calculate relative position with guards
            const rect = anchorElement.getBoundingClientRect();

            // Guard: skip zero-size anchors
            if (rect.width <= 0 || rect.height <= 0) {
              return;
            }

            const relX = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
            const relY = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));

            channel.push("cursor_move", {
              user_id: userId,
              anchor_id: anchorId,
              rel_x: relX,
              rel_y: relY,
              anchor_width: rect.width,
              anchor_height: rect.height,
              name: "Anonymous",
            });
            lastBroadcastTime = now;
          };

          document.addEventListener("mousemove", handleMouseMove, { passive: true });

          pool.add(() => {
            document.removeEventListener("mousemove", handleMouseMove);
            channel.leave();
            socket.disconnect();
          });
        })
        .catch((err) => {
          logger._debug("Failed to join channel:", err);
        });
    })
    .catch((err) => {
      logger._debug("Failed to connect to presence server:", err);
    });

  return {};
}
